import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { LegacyButton as BaseButton } from '@/components/LegacyButton';
import { NextImage } from '@/components/NextImage';
import { Typography } from '@/components/Typography';
import type { Maybe } from '@/lib/types';
import { theme } from '@/theme';
import type { BreakpointsKeys } from '@/theme/types';

import { ReactComponent as LongArrowSvg } from '@/assets/icons/longArrow.svg';

export const CARD_SIZES: Partial<Record<BreakpointsKeys, string>> = {
  lg: '680px',
  md: '550px',
  sm: '70vw',
  xs: '80vw',
};
export const BASE_CARD_SIZES: Partial<Record<BreakpointsKeys, string>> = {
  lg: '30vw',
  md: '30vw',
  sm: '70vw',
  xs: '80vw',
};
export const SECTION_BANNER_CARD_SIZES: Partial<
  Record<BreakpointsKeys, string>
> = {
  lg: '600px',
  md: '42vw',
  sm: '106vw',
  xs: '90vw',
};
export const getTextAlignment = (alignment: Maybe<string>) => {
  if (alignment === 'right') {
    return css`
      align-items: flex-end;
      justify-content: flex-end;
      text-align: right;
    `;
  }
  if (alignment === 'left') {
    return css`
      align-items: flex-start;
      justify-content: flex-start;
      text-align: left;
    `;
  }
  return css`
    align-items: center;
    text-align: center;
  `;
};

export const getContentAlignment = (alignment: Maybe<string>) => {
  switch (alignment) {
    case 'right':
      return css`
        justify-content: flex-end;
        align-items: center;
      `;
    case 'rightBottom':
      return css`
        justify-content: flex-end;
        align-items: flex-end;
      `;
    case 'left':
      return css`
        justify-content: flex-start;
        align-items: center;
      `;
    case 'leftBottom':
      return css`
        justify-content: flex-start;
        align-items: flex-end;
      `;
    case 'top':
      return css`
        justify-content: center;
        align-items: flex-start;
      `;
    case 'bottom':
      return css`
        justify-content: center;
        align-items: end;
      `;
    default:
      return css`
        justify-content: center;
        align-items: center;
      `;
  }
};
export const visibilityControl = (
  $visibleInDesktop: Maybe<boolean>,
  $visibleInMobile: Maybe<boolean>,
) => {
  return css`
    ${$visibleInDesktop === false &&
    css`
      ${theme.media.md.up} {
        display: none;
      }
    `}
    ${$visibleInMobile === false &&
    css`
      ${theme.media.md.down} {
        display: none;
      }
    `}
  `;
};

export const Background = styled(Box)<{
  $backgroundColor: string;
}>`
  height: 100%;
  width: 100%;
  inset: 0;
  position: absolute;
  z-index: -1;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
`;

export const BackgroundImage = styled(NextImage)<{
  $backgroundAlignment: Maybe<string>;
}>`
  inset: 0;
  z-index: -1;
  object-fit: cover;
  background-position: center;

  ${({ $backgroundAlignment, theme }) => css`
    ${theme.media.md.down} {
      object-position: ${$backgroundAlignment || 'center'};
    }
  `}
`;

export const Media = styled(Box)<{
  $backgroundAlignment: Maybe<string>;
}>`
  inset: 0;
  object-fit: cover;
  background-position: center;
  position: absolute;

  ${({ $backgroundAlignment, theme }) => css`
    ${theme.media.md.down} {
      object-position: ${$backgroundAlignment || 'center'};
    }
  `}
`;

export const Subheader = styled(Typography)<{
  $isCountDown?: boolean | null;
  $textColor?: Maybe<string>;
  $type?: Maybe<string>;
}>`
  text-transform: uppercase;
  ${({ $isCountDown, $textColor, theme }) => css`
    font-size: ${theme.fontSizes.h5};
    font-family: ${theme.fontFamilies.italianPlate};
    padding-bottom: ${$isCountDown ? 0 : theme.spacing(1)};
    color: ${$textColor ?? theme.colors.default};
  `}

  ${({ $isCountDown, theme }) =>
    $isCountDown &&
    css`
      margin-top: ${theme.spacing(1)};
      ${theme.media.md.up} {
        margin-top: ${theme.spacing(2)};
      }
    `}

  ${({ $textColor, $type, theme }) =>
    $type === 'Story' &&
    css`
      font-family: ${theme.fontFamilies.italianPlate};
      color: ${$textColor ?? theme.colors.default};
      font-size: ${theme.spacing(2.5)};
      margin-bottom: ${theme.spacing(1)};
      margin-top: ${theme.spacing(1)};
      padding: 0;
      ${theme.media.md.up} {
        font-size: ${theme.spacing(3)};
        margin-bottom: ${theme.spacing(2)};
      }
    `}
`;

export const Text = styled(Box)<{
  color?: string;
}>`
  word-break: break-word;
  font-weight: 700;
  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p2};
    line-height: ${theme.lineHeights.p2};
    padding-bottom: ${theme.spacing(1)};
  `}
`;

export const LongArrow = styled(LongArrowSvg)`
  vertical-align: middle;
  height: 14px;
`;

export const Button = styled(BaseButton)<{
  $color?: string;
  $lower?: boolean;
  $rowReverse?: boolean;
  $underline?: boolean;
}>`
  text-decoration: none;
  ${({ $lower }) =>
    $lower &&
    css`
      text-transform: initial;
      font-weight: 500;
    `}
  ${({ $color }) =>
    $color &&
    css`
      border: 1px solid ${$color};
      color: ${$color};
    `}
  ${({ $rowReverse }) =>
    $rowReverse &&
    css`
      display: flex;
      flex-direction: row-reverse;
    `}
`;

export const CardWrapper = styled.div`
  height: 100%;
`;
